import React, { useState, useEffect, lazy, Suspense } from "react";

import { Image } from "react-bootstrap";

import "./Home.scss";
import Web3 from "./Web3";

// AI illustration (used like web3-2.svg in the blockchain section)
import humanKnowledge from "assets/images/human-knowleadge.svg";

// Lazy load Spline to dramatically speed up dev server startup
// (Spline + Three.js is very heavy during initial compilation)
const Spline = lazy(() => import("@splinetool/react-spline"));

// Single-page landing content only.
// Order: Hero → AI (new) → From Web 1.0 to Web3

// ── Formspree endpoint (create a free form at https://formspree.io) ──
const FORM_ACTION = "https://formspree.io/f/xeedbkne";

const initialForm = { name: "", email: "", message: "" };

export default function Home() {
  const [showSpline, setShowSpline] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [formStatus, setFormStatus] = useState("idle"); // idle | sending | ok | error

  useEffect(() => {
    const checkSize = () => setShowSpline(window.innerWidth >= 992);
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  return (
    <main className="main">
      {/* ==================== HERO ==================== */}
      <div className="bg-hero text-white position-relative overflow-hidden">
        <div className="container-lg">
          <div
            className="home-blockchain-solution position-relative"
            style={{ zIndex: 1 }}
          >
            <div
              style={{ zIndex: 2 }}
              className="col-lg-7 text-center text-lg-start"
            >
              <h1 className="heading-g2" style={{ fontSize: "3rem", lineHeight: 1.1 }}>
                Grindy Technologies
              </h1>
              <p className="heading-4 fw-normal mt-2" style={{ fontSize: "1.5rem" }}>
                Building Technologies for Freedom
              </p>
              <p className="mt-2 mb-0" style={{ fontSize: "1.1rem", opacity: 0.9 }}>
                AI and blockchain products.
              </p>

              {/* Two slogans */}
              <div className="mt-4" style={{ fontSize: "1.1rem", opacity: 0.95 }}>
                <div>Keep Grinding. Keep Training.</div>
                <div>Keep the grind. Keep training.</div>
              </div>
            </div>
          </div>

          {/* Spline 3D background - desktop only + lazy loaded for fast dev startup */}
          {showSpline && (
            <div
              className="position-absolute w-100 h-100 top-0 pe-none d-none d-lg-block"
              style={{ transform: "translateX(10%)" }}
            >
              <Suspense fallback={null}>
                <Spline
                  scene="https://prod.spline.design/XWG6AdostEl-qDVx/scene.splinecode"
                />
              </Suspense>
            </div>
          )}
        </div>
      </div>

      {/* AI Section */}
      <div className="home-ai">
        <div className="container-lg">
          <div className="row justify-content-between align-items-center flex-column-reverse flex-lg-row">
            {/* Text side - matching Web3.jsx structure */}
            <div className="col-lg-6 mt-5 mt-lg-0 text-center text-lg-start">
              <h2 className="mb-3 heading-4">Unlocking What Was Never Imagined</h2>

              <p className="lead text-body-2 d-inline-flex flex-column">
                {`AI allows humans to leverage centuries of human knowledge and unlock things we never imagined. In the early days, knowledge moved slowly from person to person. Today, AI makes the full power of human knowledge instantly accessible to everyone.`}

                <strong className="lead text-body-2 mt-4">
                  {`We provide solutions that hold the key strengths of current systems and combine the benefits of next-level AI technologies that we believe AI is the way people are truly meant to think, create, and unlock their full potential.`}
                </strong>

                <span className="lead text-body-2 mt-4">
                  We ship AI products people use.
                </span>
              </p>
            </div>

            {/* Visual side - human-knowleadge.svg (used exactly like web3-2.svg) */}
            <div className="col-lg-6 col-md-6 col-8 text-center">
              <Image
                src={humanKnowledge}
                alt="AI & Human Knowledge"
                style={{ maxWidth: "420px", width: "100%" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* FROM WEB 1.0 TO WEB3 */}
      <Web3 />

      {/* ==================== OUR PRODUCTS ==================== */}
      <div className="our-products">
        <div className="container-lg">
          <h2 className="heading-4 text-center mb-2">Our Products</h2>
          <p className="text-center text-body-2 mb-5">
            AI and blockchain products we build and ship.
          </p>

          <div className="row g-4 justify-content-center">
            {/* Quac */}
            <div className="col-lg-4 col-md-6">
              <div className="product-card">
                <a
                  href="https://quac.vn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="product-card-logo product-card-logo--icon"
                >
                  <img
                    src="/quac-logo.svg"
                    alt="Quac — AI team chat"
                  />
                </a>
                <h3 className="product-card-name">Quac</h3>
                <p className="product-card-desc">
                  Free team chat that works faster with AI built in
                </p>
                <a
                  href="https://quac.vn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  Visit Quac &rarr;
                </a>
              </div>
            </div>

            {/* Cook */}
            <div className="col-lg-4 col-md-6">
              <div className="product-card">
                <a
                  href="https://letcook.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="product-card-logo product-card-logo--icon"
                >
                  <img
                    src="/cook-logo.svg"
                    alt="Cook — fast coding agent for CLI and desktop"
                  />
                </a>
                <h3 className="product-card-name">Cook</h3>
                <p className="product-card-desc">
                  A fast, open source coding agent for your terminal and desktop
                </p>
                <a
                  href="https://letcook.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  Visit Cook &rarr;
                </a>
              </div>
            </div>

            {/* Gafi */}
            <div className="col-lg-4 col-md-6">
              <div className="product-card">
                <a
                  href="https://github.com/grindytech/gafi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="product-card-logo"
                >
                  <img
                    src="/gafi-logo.svg"
                    alt="Gafi — game finance blockchain"
                  />
                </a>
                <h3 className="product-card-name">Gafi</h3>
                <p className="product-card-desc">
                  The Network of Game Finance. High-performance blockchain
                  platform built for games and decentralized apps.
                </p>
                <a
                  href="https://github.com/grindytech/gafi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  GitHub &rarr;
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== BUILD WITH US ==================== */}
      <div className="build-with-us">
        <div className="container-lg">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              <h2 className="heading-4 text-center mb-2">Build With Us</h2>
              <p className="text-center text-body-2 mb-4">
                Have an idea, a project, or just want to say hello? We'd love
                to hear from you.
              </p>

              {formStatus === "ok" ? (
                <div className="form-success">
                  <div className="form-success-icon">&#10003;</div>
                  <h3 className="h5">Message sent!</h3>
                  <p>Thanks for reaching out — we'll get back to you soon.</p>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      setForm(initialForm);
                      setFormStatus("idle");
                    }}
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form
                  className="contact-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setFormStatus("sending");
                    fetch(FORM_ACTION, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(form),
                    })
                      .then((r) => {
                        if (r.ok) setFormStatus("ok");
                        else setFormStatus("error");
                      })
                      .catch(() => setFormStatus("error"));
                  }}
                >
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="cu-name" className="form-label">
                        Name
                      </label>
                      <input
                        id="cu-name"
                        type="text"
                        className="form-control"
                        placeholder="Your name"
                        required
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="cu-email" className="form-label">
                        Email
                      </label>
                      <input
                        id="cu-email"
                        type="email"
                        className="form-control"
                        placeholder="you@example.com"
                        required
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label htmlFor="cu-msg" className="form-label">
                      Message
                    </label>
                    <textarea
                      id="cu-msg"
                      className="form-control"
                      rows={4}
                      placeholder="Tell us about your idea..."
                      required
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                    />
                  </div>

                  {formStatus === "error" && (
                    <div className="alert alert-danger mt-3 mb-0 py-2">
                      Something went wrong. Please try again or email us
                      directly at{" "}
                      <a href="mailto:hello@grindy.io">hello@grindy.io</a>.
                    </div>
                  )}

                  <div className="mt-3 text-center">
                    <button
                      type="submit"
                      className="btn btn-primary px-5"
                      disabled={formStatus === "sending"}
                    >
                      {formStatus === "sending" ? (
                        <>Sending&hellip;</>
                      ) : (
                        "Send Message"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
